import { compressImage as compressImageBase } from '../utils/imageCompressor';
import {
  formatImageSizePair,
  ImagePickValidationError,
  validateCompressedImageFile,
  validateOriginalImageFile,
} from './imagePickRules';

export {
  IMAGE_INPUT_ACCEPT,
  IMAGE_PICK_TOAST,
  ImagePickValidationError,
  formatImageSize,
  formatImageSizePair,
  validateOriginalImageFile,
  validateCompressedImageFile,
} from './imagePickRules';

export const getOptimizedImageUrl = (url, {
  width = 400,
  height = null,
  quality = 80,
  format = 'webp',
} = {}) => {
  if (!url) return null;
  if (!url.includes('supabase')) return url;
  if (url.includes('?')) return url;

  const params = new URLSearchParams();
  params.set('width', width);
  if (height) params.set('height', height);
  params.set('quality', quality);
  params.set('format', format);

  return `${url}?${params.toString()}`;
};

export const imagePresets = {
  thumbnail: (url) => getOptimizedImageUrl(url, {
    width: 200, height: 200, quality: 75,
  }),
  detail: (url) => getOptimizedImageUrl(url, {
    width: 800, quality: 85,
  }),
  avatar: (url) => getOptimizedImageUrl(url, {
    width: 100, height: 100, quality: 80,
  }),
  banner: (url) => getOptimizedImageUrl(url, {
    width: 480, quality: 80,
  }),
  card: (url) => getOptimizedImageUrl(url, {
    width: 400, height: 300, quality: 80,
  }),
};

/**
 * WebP 압축 업로드용 (최대 변 maxWidth px).
 * @param {File} file
 * @param {number} [maxWidth]
 * @returns {Promise<File|Blob>}
 */
export async function compressImage(file, maxWidth = 1200) {
  if (!file) return file;
  const pre = validateOriginalImageFile(file);
  if (!pre.ok) throw new ImagePickValidationError(pre.toast);
  const compressed = await compressImageBase(file);
  if (maxWidth >= 1280) {
    const post = validateCompressedImageFile(compressed);
    if (!post.ok) throw new ImagePickValidationError(post.toast);
    return compressed;
  }
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, 1);
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          if (!blob) {
            const post = validateCompressedImageFile(compressed);
            if (!post.ok) reject(new ImagePickValidationError(post.toast));
            else resolve(compressed);
            return;
          }
          const base = (file.name || 'image').replace(/\.[^.]+$/i, '');
          const next = new File([blob], `${base}.webp`, { type: 'image/webp' });
          const post = validateCompressedImageFile(next);
          if (!post.ok) reject(new ImagePickValidationError(post.toast));
          else resolve(next);
        },
        'image/webp',
        0.85,
      );
    };
    img.onerror = () => {
      const post = validateCompressedImageFile(compressed);
      if (!post.ok) reject(new ImagePickValidationError(post.toast));
      else resolve(compressed);
    };
    img.src = URL.createObjectURL(compressed);
  });
}

/**
 * 파일 선택 → 검증 → WebP 압축 (QuickImageUpload 등)
 * @param {File} file
 * @param {{ compress?: boolean, maxWidth?: number }} [opts]
 * @returns {Promise<{ ok: true, file: File, sizeLabel: string } | { ok: false, toast: string }>}
 */
export async function processPickedImageFile(file, opts = {}) {
  const { compress = true, maxWidth = 1200 } = opts;
  const pre = validateOriginalImageFile(file);
  if (!pre.ok) return { ok: false, toast: pre.toast };
  const originalSize = file.size;
  let out = file;
  if (compress) {
    try {
      out = await compressImage(file, maxWidth);
    } catch (err) {
      if (err instanceof ImagePickValidationError) {
        return { ok: false, toast: err.toastMessage };
      }
      throw err;
    }
  }
  const post = validateCompressedImageFile(out);
  if (!post.ok) return { ok: false, toast: post.toast };
  return {
    ok: true,
    file: out,
    sizeLabel: formatImageSizePair(originalSize, out.size),
  };
}
